import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Button, Spinner} from 'react-bootstrap'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {useForm} from 'react-hook-form'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import api, {BankData, BankRequestStatus, ValidationError} from '../service/api'
import {keys} from '../utils'
import {CheckBox, FormField, FormGroup, MyForm, SubmitButton} from '../forms'
import {isValidBIC, isValidIBAN} from 'ibantools'
import {useI18n} from '../i18n'

export const BankDataIdentIntroScreen = () => {
    const {t} = useI18n()
    const [searchParams] = useSearchParams()
    const identUrl = searchParams.get('identUrl')

    return (
        <div>
            <h2>{t('BankDataIdentIntroScreen.head')}</h2>
            <p>{t('BankDataIdentIntroScreen.copy')}</p>
            <div className="mt-4 pb-4">
                <Button
                    variant="primary"
                    size="lg"
                    disabled={!identUrl}
                    onClick={() => { if (identUrl) window.location.href = identUrl }}
                >
                    {t('BankDataIdentIntroScreen.button')}
                </Button>
            </div>
        </div>
    )
}

const NON_EURO = ['DK', 'BG', 'HR', 'CZ', 'HU', 'PL', 'RO', 'SE', 'NO', 'IS', 'LI']
export const BankDataScreen = () => {
    const { t } = useI18n()
    const [info, setInfo] = useState('')
    const navigate = useNavigate()

    const methods = useForm<BankData & { acceptEuro: boolean }>({
        mode: 'all',
        defaultValues: {
            iban: '',
            bic: '',
            bank: ''
        },
    })

    const {reset, formState, setError, clearErrors, watch} = methods

    const iban = watch('iban')
    const acceptEuro = watch('acceptEuro')

    const [showEuroWarning, setShowEuroWarning] = useState(false)
    const [isUpdate, setIsUpdate] = useState(false)
    const [submitting, setSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState(false)

    // test ibans: DK5000400440116243 DE12500105170648489890
    useEffect(() => {
        setShowEuroWarning(!!iban && NON_EURO.some(s => iban.startsWith(s)))
    }, [iban])

    useEffect(() => {
        api.getRequestStatus().then(s => {
            if (s.open) {
                setInfo(s.info || '')
                const bankData = (s as BankRequestStatus).bankData
                setIsUpdate(!!bankData?.iban)
                reset(bankData)
            } else {
                navigate('/notopen')
            }
        })
    }, [navigate, reset])

    const onSubmit = useCallback((data: any) => {
        clearErrors()
        setSubmitError(false)
        setSubmitting(true)
        api.updateBankData(data).then(result => {
            if (result?.redirectUrl) {
                window.location.href = result.redirectUrl
            } else {
                navigate('/bankdata/done')
            }
        }).catch((v: ValidationError<BankData>) => {
            if (v?.data && keys(v.data).length > 0) {
                keys(v.data).forEach(k => setError(k, {type: 'server', message: v.data[k]}))
            } else {
                setSubmitError(true)
            }
        }).finally(() => setSubmitting(false))
    }, [clearErrors, info, navigate, setError])
    return (
        <div>
            <h2>{t('BankDataScreen.head.' + (isUpdate ? 'update' : 'create'))}</h2>
            <p>{info}</p>
            <MyForm onSubmit={onSubmit} methods={methods}>
                <FormGroup>
                    <FormField name="iban"
                               rules={{required: true, validate: {iban: (v: string) => isValidIBAN(v)}}}/>
                </FormGroup>
                <FormGroup>
                    <FormField name="bic"
                               rules={{required: true, validate: {bic: (v: string) => isValidBIC(v)}}}/>
                </FormGroup>
                <FormGroup>
                    <FormField name="bank" rules={{required: true, minLength: 5}}/>
                </FormGroup>
                {showEuroWarning && <FormGroup>
                    <FormField type={CheckBox} name="acceptEuro"
                               rules={{validate: (v: boolean) => (!showEuroWarning || v) || ' '}}
                               checkBoxLabel={<i>{t('BankDataScreen.euroWarning')}</i>}/>
                </FormGroup>}
                {submitError && <Alert variant="danger">{t('BankDataScreen.submitError')}</Alert>}
                <div className="mt-5 pb-4">
                    <SubmitButton disabled={submitting || ((showEuroWarning || !acceptEuro) && !formState.isValid)} type="submit">
                        {submitting ? <Spinner size="sm" className="me-2"/> : null}{t('BankDataScreen.submit')}
                    </SubmitButton>
                </div>
            </MyForm>
        </div>
    )
}
export const BankDataDoneScreen = () => {
    const { t } = useI18n()
    return (
        <>
            <h2>{t('BankDataScreen.thankYou')}</h2>
        </>
    )
}

const RedirectCountdown = ({url}: {url: string}) => {
    const {t} = useI18n()
    const [count, setCount] = useState(10)

    useEffect(() => {
        if (count <= 0) {
            window.location.href = url
            return
        }
        const timer = setTimeout(() => setCount(c => c - 1), 1000)
        return () => clearTimeout(timer)
    }, [count, url])

    return (
        <div className="mt-3">
            <p>{t('redirectCountdown', {count})}</p>
            <a href={url}>{t('redirectNow')}</a>
        </div>
    )
}

export const BankDataIdentSuccessScreen = () => {
    const {t} = useI18n()
    const [searchParams] = useSearchParams()
    const requestId = searchParams.get('requestId')
    const [status, setStatus] = useState<string | null>(null)
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null)

    const fetchStatus = useCallback(() => {
        if (!requestId) return
        api.getBankDataIdentStatus(requestId).then(r => {
            setStatus(r?.status ?? null)
            if (r?.redirectUrl) setRedirectUrl(r.redirectUrl)
        })
    }, [requestId])

    useEffect(() => {
        if (!requestId || status === 'DONE' || status === 'FAILED') return
        const timer = setTimeout(fetchStatus, status === null ? 0 : 3000)
        return () => clearTimeout(timer)
    }, [requestId, status, fetchStatus])

    useEffect(() => {
        if (!requestId || status === 'DONE' || status === 'FAILED') return
        const onVisible = () => {
            if (document.visibilityState === 'visible') fetchStatus()
        }
        document.addEventListener('visibilitychange', onVisible)
        window.addEventListener('pageshow', onVisible)
        return () => {
            document.removeEventListener('visibilitychange', onVisible)
            window.removeEventListener('pageshow', onVisible)
        }
    }, [requestId, status, fetchStatus])

    if (status === 'DONE') return (
        <div className="text-center mt-4">
            <FontAwesomeIcon icon="check-circle" size="4x" color="green"/>
            <h2 className="mt-3">{t('BankDataIdentSuccessScreen.done')}</h2>
            {redirectUrl ? <RedirectCountdown url={redirectUrl}/> : <p className="mt-3">{t('BankDataIdentSuccessScreen.closeHint')}</p>}
        </div>
    )
    if (status === 'FAILED') return (
        <div className="text-center mt-4">
            <FontAwesomeIcon icon="times-circle" size="4x" color="red"/>
            <h2 className="mt-3">{t('BankDataIdentSuccessScreen.failed')}</h2>
            {redirectUrl && <RedirectCountdown url={redirectUrl}/>}
        </div>
    )
    return (
        <div className="text-center mt-4">
            <FontAwesomeIcon icon="sync" spin size="4x"/>
            <h2 className="mt-3">{t('BankDataIdentSuccessScreen.loading')}</h2>
        </div>
    )
}

export const BankDataIdentErrorScreen = () => {
    const {t} = useI18n()
    const [searchParams] = useSearchParams()
    const requestId = searchParams.get('requestId')
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!requestId) return
        api.getBankDataIdentStatus(requestId).then(r => {
            if (r?.redirectUrl) setRedirectUrl(r.redirectUrl)
        })
    }, [requestId])

    return (
        <div className="text-center mt-4">
            <FontAwesomeIcon icon="times-circle" size="4x" color="red"/>
            <h2 className="mt-3">{t('BankDataIdentErrorScreen.message')}</h2>
            {redirectUrl && <RedirectCountdown url={redirectUrl}/>}
        </div>
    )
}

export const BankDataIdentAbortedScreen = () => {
    const {t} = useI18n()
    const [searchParams] = useSearchParams()
    const requestId = searchParams.get('requestId')
    const [redirectUrl, setRedirectUrl] = useState<string | null>(null)

    useEffect(() => {
        if (!requestId) return
        api.getBankDataIdentStatus(requestId).then(r => {
            if (r?.redirectUrl) setRedirectUrl(r.redirectUrl)
        })
    }, [requestId])

    return (
        <div className="text-center mt-4">
            <FontAwesomeIcon icon="ban" size="4x" color="red"/>
            <h2 className="mt-3">{t('BankDataIdentAbortedScreen.message')}</h2>
            {redirectUrl && <RedirectCountdown url={redirectUrl}/>}
        </div>
    )
}
